/**
 * Community Garden — Connection Status Component (GARDEN-165)
 * 
 * Implements a mutually exclusive connection state machine with priority:
 * 1. failed (highest priority)
 * 2. disconnected
 * 3. refreshing
 * 4. connecting
 * 5. connected (lowest priority)
 * 
 * Only ONE state is displayed at any time.
 */

export type ConnectionState = 
  | 'connected'
  | 'connecting'
  | 'refreshing'
  | 'failed'
  | 'disconnected';

export interface ConnectionStatus {
  state: ConnectionState;
  error: string | null;
  lastUpdated: number;
}

class ConnectionStatusManager {
  private status: ConnectionStatus = {
    state: 'disconnected',
    error: null,
    lastUpdated: Date.now(),
  };

  private subscribers: Set<(state: ConnectionState, previousState: ConnectionState) => void> = new Set();

  /**
   * Get current connection status
   */
  getStatus(): ConnectionStatus {
    return { ...this.status };
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.status.state;
  }

  /**
   * Subscribe to connection state changes
   */
  subscribe(subscriber: (state: ConnectionState, previousState: ConnectionState) => void): () => void {
    this.subscribers.add(subscriber);
    return () => {
      this.subscribers.delete(subscriber);
    };
  }

  /**
   * Update connection state with priority enforcement
   */
  private updateState(newState: ConnectionState, error: string | null = null): void {
    const previousState = this.status.state;
    
    // Don't update if state hasn't changed (idempotent)
    if (previousState === newState && this.status.error === error) {
      return;
    }

    // Apply priority rules: failed > disconnected > refreshing > connecting > connected
    // If trying to transition to a lower priority state while in a higher priority state,
    // keep the higher priority state (except failed always wins)
    const priority: Record<ConnectionState, number> = {
      'failed': 5,
      'disconnected': 4,
      'refreshing': 3,
      'connecting': 2,
      'connected': 1,
    };

    // Failed state always wins (highest priority)
    if (newState === 'failed') {
      // Always transition to failed state
    }
    // If current state is failed, only allow transition to disconnected or connecting
    else if (previousState === 'failed' && newState !== 'disconnected' && newState !== 'connecting') {
      if (import.meta.env.DEV) {
        console.log(`[ConnectionStatus] Can only transition from failed to disconnected or connecting, not to ${newState}`);
      }
      return;
    }
    // Allow normal state transitions
    else {
      // All transitions are allowed - the state machine ensures consistency
    }

    // Update status
    this.status = {
      state: newState,
      error,
      lastUpdated: Date.now(),
    };

    // Notify subscribers
    this.subscribers.forEach(subscriber => {
      subscriber(newState, previousState);
    });
  }

  /**
   * Public API for state transitions
   */

  /** WebSocket connected successfully */
  connected(): void {
    this.updateState('connected');
  }

  /** Starting to connect to WebSocket */
  connecting(): void {
    this.updateState('connecting');
  }

  /** Refreshing/loading data */
  refreshing(): void {
    this.updateState('refreshing');
  }

  /** Connection failed */
  failed(message: string): void {
    this.updateState('failed', message);
  }

  /** WebSocket disconnected */
  disconnected(): void {
    this.updateState('disconnected');
  }

  /**
   * Get display text for current state
   */
  getDisplayText(): string {
    switch (this.status.state) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting';
      case 'refreshing':
        return 'Refreshing';
      case 'failed':
        return this.status.error || 'Connection failed';
      case 'disconnected':
        return 'Disconnected';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get CSS class for current state
   */
  getCssClass(): string {
    switch (this.status.state) {
      case 'connected':
        return 'connected';
      case 'connecting':
        return 'connecting';
      case 'refreshing':
        return 'loading';
      case 'failed':
        return 'error';
      case 'disconnected':
        return 'disconnected';
      default:
        return 'unknown';
    }
  }

  /**
   * Check if we should show reconnect button
   */
  shouldShowReconnectButton(): boolean {
    // SCOUT-089: Only show reconnect button after auto-reconnect has fully failed.
    // Transient 'disconnected' state during navigation or auto-reconnect should not alarm the user.
    return this.status.state === 'failed';
  }

  /**
   * Check if we're in an error state
   */
  isError(): boolean {
    return this.status.state === 'failed';
  }

  /**
   * Check if we're currently connecting or loading
   */
  isLoading(): boolean {
    return this.status.state === 'connecting' || this.status.state === 'refreshing';
  }

  /**
   * Check if we're fully connected
   */
  isConnected(): boolean {
    return this.status.state === 'connected';
  }

  /**
   * Reset to initial state
   */
  reset(): void {
    this.updateState('disconnected');
  }
}

// Singleton instance
export const connectionStatus = new ConnectionStatusManager();